import { useTranslation } from 'next-i18next';
import {
  FC, ReactNode, useCallback, useMemo,
} from 'react';
import { DropdownOption } from 'src/common/components/DropdownDS22';
import Typography from 'src/common/components/Typography';
import { Sort, useListingsPageContext } from 'src/admin/listing-editor/providers/ListingsPageProvider';
import ActionsDropdown from 'src/common/components/ActionsDropdown';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import styles from './ListingsContent.module.scss';

type Props = {
  children: ReactNode;
};

const ListingsContent: FC<Props> = ({ children }) => {
  const [t] = useTranslation('admin-listings');
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const { ownerListings, setSort, sort: sortQuery } = useListingsPageContext();
  const hasSnoozedListings = !ownerListings.every(
    ({ snooze_start_date, snooze_end_date }) => !snooze_start_date && !snooze_end_date,
  );

  const actionOptions = useMemo((): DropdownOption[] => [
    {
      id: 'asc',
      dropdownLabel: t('landing-page--sort-asc'),
      selectedLabel: t('landing-page--sort-asc'),
    },
    {
      id: 'desc',
      dropdownLabel: t('landing-page--sort-desc'),
      selectedLabel: t('landing-page--sort-desc'),
    },
    {
      id: 'new',
      dropdownLabel: t('landing-page--sort-newest'),
      selectedLabel: t('landing-page--sort-newest'),
    },
    {
      id: 'old',
      dropdownLabel: t('landing-page--sort-oldest'),
      selectedLabel: t('landing-page--sort-oldest'),
    },
    {
      id: 'snzd',
      dropdownLabel: t('landing-page--sort-snoozed'),
      selectedLabel: t('landing-page--sort-snoozed'),
      disabled: !hasSnoozedListings,
    },
    {
      id: 'pub',
      dropdownLabel: t('landing-page--sort-published'),
      selectedLabel: t('landing-page--sort-published'),
    },
    {
      id: 'unpub',
      dropdownLabel: t('landing-page--sort-unpublished'),
      selectedLabel: t('landing-page--sort-unpublished'),
    },
  ], [t, hasSnoozedListings]);

  const handleOnSelect = useCallback(
    (item: DropdownOption) => {
      setSort(item.id as Sort);
    },
    [setSort],
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography type="h2" size="heading-5" weight="semibold" className={styles.title}>
          {t(isNavUpdateEnabled ? 'landing-page--heading-new' : 'landing-page--heading')}
        </Typography>
        <ActionsDropdown
          options={actionOptions}
          handleSelect={handleOnSelect}
          mode="menu"
          defaultOption={actionOptions.find((option) => option.id === sortQuery)}
        />
      </div>
      {children}
    </div>
  );
};

export default ListingsContent;
